import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import { Link, useParams } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../api.js';
import { Card } from '../components/ui.js';

interface DocMeta {
  slug: string;
  title: string;
  group: string;
  order: number;
}

/** Tailwind-styled renderers for the markdown elements the docs use (dark/light). */
const mdComponents: Components = {
  h1: ({ children }) => <h1 className="mt-2 text-2xl font-bold tracking-tight">{children}</h1>,
  h2: ({ children }) => (
    <h2 className="mt-8 border-b border-neutral-200 pb-1.5 text-xl font-semibold dark:border-neutral-800">
      {children}
    </h2>
  ),
  h3: ({ children }) => <h3 className="mt-6 text-base font-semibold">{children}</h3>,
  p: ({ children }) => (
    <p className="mt-3 leading-relaxed text-neutral-700 dark:text-neutral-300">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="mt-3 list-disc space-y-1.5 pl-6 text-neutral-700 dark:text-neutral-300">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="mt-3 list-decimal space-y-1.5 pl-6 text-neutral-700 dark:text-neutral-300">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  a: ({ href, children }) => (
    <a href={href} className="font-medium text-emerald-600 hover:underline dark:text-emerald-400">
      {children}
    </a>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-neutral-900 dark:text-neutral-100">{children}</strong>
  ),
  blockquote: ({ children }) => (
    <blockquote className="mt-4 border-l-2 border-emerald-400 bg-emerald-50/40 py-1 pl-4 text-neutral-700 dark:bg-emerald-950/20 dark:text-neutral-300">
      {children}
    </blockquote>
  ),
  code: ({ className, children }) => {
    // Block code (has a language class) vs inline code.
    if (className?.includes('language-')) {
      return <code className={className}>{children}</code>;
    }
    return (
      <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-[0.85em] text-emerald-700 dark:bg-neutral-800 dark:text-emerald-300">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="mt-4 overflow-x-auto rounded-lg bg-neutral-900 p-4 text-sm leading-relaxed text-neutral-100 dark:bg-neutral-950">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-neutral-200 bg-neutral-50 px-3 py-2 text-left font-semibold dark:border-neutral-800 dark:bg-neutral-900">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-neutral-200 px-3 py-2 align-top dark:border-neutral-800">
      {children}
    </td>
  ),
  hr: () => <hr className="my-8 border-neutral-200 dark:border-neutral-800" />,
};

export function Docs() {
  const { baseUrl, token } = useAuth();
  const { slug } = useParams();
  const active = slug || 'index';
  const [list, setList] = useState<DocMeta[]>([]);
  const [markdown, setMarkdown] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const authHeaders = useMemo(
    () => ({ authorization: `Bearer ${token}` }) as Record<string, string>,
    [token],
  );

  // Sidebar list (once).
  useEffect(() => {
    fetch(`${baseUrl.replace(/\/$/, '')}/docs`, { headers: authHeaders })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: { docs: DocMeta[] }) =>
        setList([...d.docs].sort((a, b) => a.group.localeCompare(b.group) || a.order - b.order)),
      )
      .catch(() => {});
  }, [baseUrl, authHeaders]);

  // Selected doc body.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    fetch(`${baseUrl.replace(/\/$/, '')}/docs/${active}`, { headers: authHeaders })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: { markdown: string }) => {
        if (alive) setMarkdown(d.markdown);
      })
      .catch(() => alive && setError('Could not load this page.'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [baseUrl, authHeaders, active]);

  // Group the sidebar entries, preserving group order of first appearance.
  const groups = useMemo(() => {
    const m = new Map<string, DocMeta[]>();
    for (const d of list) m.set(d.group, [...(m.get(d.group) ?? []), d]);
    return [...m.entries()];
  }, [list]);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold">Integration & engine docs</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Private documentation for customers and integration partners — not published publicly.
        </p>
      </div>
      <div className="grid gap-6 md:grid-cols-[15rem_1fr]">
        {/* Sidebar */}
        <nav className="md:sticky md:top-20 md:self-start">
          {groups.map(([group, docs]) => (
            <div key={group} className="mb-5">
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-widest text-neutral-400">
                {group}
              </div>
              <ul className="space-y-0.5">
                {docs.map((d) => (
                  <li key={d.slug}>
                    <Link
                      to={d.slug === 'index' ? '/docs' : `/docs/${d.slug}`}
                      className={`block rounded-lg px-3 py-1.5 text-sm transition ${
                        active === d.slug
                          ? 'bg-emerald-600 text-white'
                          : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800'
                      }`}
                    >
                      {d.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Content */}
        <Card className="min-w-0">
          {error ? (
            <p className="text-sm text-rose-600">{error}</p>
          ) : loading ? (
            <p className="text-sm text-neutral-500">Loading…</p>
          ) : (
            <article className="min-w-0">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                {markdown}
              </ReactMarkdown>
            </article>
          )}
        </Card>
      </div>
    </div>
  );
}
